/**
 * Shared video/recording matching helpers.
 *
 * Used by every report view (run / scout / heal) that needs to join a
 * Playwright test attachment to a DP recording blob. By centralizing here
 * we guarantee identical behaviour across views — change once, applies
 * to all. See `ProjectDashboard.handleWatchVideo` (run), `ScoutReportView`,
 * and any future heal-attachment lookups for usage.
 */

import type { ProjectVideoFile, RecordingItem } from "../api/models/ApiModels";

/** Convert DP `RecordingItem[]` (the raw API shape) to the
 *  `ProjectVideoFile[]` shape that all video-matching helpers consume. */
export function mapRecordingsToVideoFiles(recordings?: RecordingItem[] | null): ProjectVideoFile[] {
    return (recordings ?? [])
        .filter((r) => r.blobPath.toLowerCase().endsWith(".webm") && !!r.sasUri)
        .map((r) => ({
            testCaseName: r.testName ?? null,
            url: r.sasUri ?? null,
            blobName: r.blobPath,
            uploadedAt: undefined,
            fileSize: undefined,
        }));
}

/** Strip everything up to and including the FIRST `test-results/` segment.
 *  Both Playwright attachment paths (e.g. `/home/runner/.../test-results/foo/video.webm`)
 *  and DP recording blob paths (e.g. `sessions/<id>/test-results/foo/video.webm`)
 *  reduce to the same suffix `test-results/foo/video.webm`. */
export function testResultsSuffix(p: string): string {
    const norm = p.replace(/\\/g, "/");
    if (!norm.includes("test-results/")) return norm;
    return norm.split("test-results/").slice(1).join("test-results/");
}

/** Find the recording whose blob matches the given Playwright attachment
 *  path by `test-results/...` suffix equality. Used identically by all views. */
export function findRecordingForVideoPath(
    videoPath: string | undefined | null,
    videoFiles: ProjectVideoFile[]
): ProjectVideoFile | undefined {
    if (!videoPath) return undefined;
    const localSuffix = testResultsSuffix(videoPath);
    return videoFiles.find((v) => {
        if (!v.blobName) return false;
        return testResultsSuffix(v.blobName) === localSuffix;
    });
}
